import React, { useState, useMemo, useEffect, useRef } from 'react'
import { Outlet, useOutletContext, useLocation } from 'react-router-dom'
import { Content } from './style'
import {
  OptionsButton,
  LeftSide,
  RightSide,
  SectionName,
  SubHeaderContainer,
  ButtonCreate,
  RefreshIcon,
  RefreshIconContainer,
  TabsDiv,
  Tab,
} from '../../../components/styles/subheader-style'
import refresh_act from '../../../assets/refresh-active.svg'
import { useNavigate } from 'react-router-dom'
import { Plus, Filter as FilterIcon } from 'lucide-react'
import { ViewModeSelector } from '../Pipelines/PipelineControls'
import stock from '../../../assets/stock/TableMovementHeader/list-active.svg'
import resource from '../../../assets/home/resource.svg'
import length from '../../../assets/home/length.svg'
import {
  EMPTY_PRODUCT_FILTERS,
  type ProductDevelopmentProductFilters,
} from '../ProductDevelopment/productDevelopmentFilters'
import { usePersistedFilters } from '../../../hooks/usePersistedFilters'
import FilterMenu from '../../../components/SubHeaderFilter/FilterMenu'
import AppliedFiltersDisplay from '../../../components/SubHeaderFilter/AppliedFiltersDisplay'
import { Filter, Header } from '../../../components/SubHeaderFilter/types'
import { FilterWrapper } from '../../../components/styles/subheader-style'

const COMPOSITION_VIEW_MODE_STORAGE_KEY = 'product-development-composition-view-mode'

export const compositionHeaders: Header[] = [
  { key: 'name', label: 'Produto', type: 'string' },
  { key: 'code', label: 'Referência', type: 'string' },
  { key: 'collection', label: 'Coleção', type: 'string' },
  { key: 'family', label: 'Família', type: 'string' },
  { key: 'episode', label: 'Episódio', type: 'string' },
  { key: 'group', label: 'Grupo', type: 'string' },
  { key: 'colors', label: 'Cor', type: 'string' },
  { key: 'grades', label: 'Grade', type: 'string' }
]

interface TypeMainOutlet {
  search: string
}

export interface ProductCompositionOutletType {
  refresh: boolean
  search: string
  viewMode: 'board' | 'table'
  productFilters: ProductDevelopmentProductFilters
  appliedFilters: Filter[]
  isLoading?: boolean
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>
}
const compositionTabs = [
  { path: '/panel/stock', image: stock, name: 'Tecido' },
  { path: '/panel/stock/resource', image: resource, name: 'Aviamentos' },
  { path: '/panel/composition-product', image: length, name: 'Ficha técnica de produto' },
]

export const ProductCompositionSubHeader: React.FC<{
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>
  isLoading?: boolean
  viewMode: 'board' | 'table'
  setViewMode: React.Dispatch<React.SetStateAction<'board' | 'table'>>
  appliedFilters: Filter[]
  onApplyFilter: (f: Filter) => void
}> = ({ setRefresh, isLoading, viewMode, setViewMode, appliedFilters, onApplyFilter }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const filterButtonRef = useRef<HTMLDivElement | null>(null)

  const activeIndex = (() => {
    if (location.pathname.includes('/panel/stock/resource')) return 1
    if (location.pathname.includes('/panel/composition-product')) return 2
    return 0
  })()

  const isFormPage =
    location.pathname.includes('/panel/composition-product/create') ||
    location.pathname.includes('/panel/composition-product/edit')

  return (
    <SubHeaderContainer style={{ zIndex: 20 }}>
      <LeftSide>
        <SectionName>Estoque</SectionName>
        {!isFormPage && (
          <TabsDiv>
            {compositionTabs.map((tab, index) => (
              <Tab
                key={index}
                $isFirst={index === 0}
                $isLast={index === compositionTabs.length - 1}
                $isActive={index === activeIndex}
                onClick={() => {
                  if (index !== activeIndex) {
                    navigate(tab.path)
                  }
                }}
              >
                <img src={tab.image} alt={`${tab.name}-icon`} />
                {tab.name}
              </Tab>
            ))}
          </TabsDiv>
        )}
      </LeftSide>
      <RightSide style={{ position: 'relative', alignItems: 'center' }}>
        {!isFormPage && (
          <ViewModeSelector
            value={viewMode}
            onChange={setViewMode}
            boardLabel='Quadro'
            tableLabel='Tabela'
          />
        )}
        <RefreshIconContainer onClick={() => setRefresh(prev => !prev)} title="Atualizar">
          <RefreshIcon src={refresh_act} alt="Atualizar" $spinning={isLoading} />
        </RefreshIconContainer>
        {!isFormPage && viewMode === 'table' && (
          <FilterWrapper>
            <OptionsButton ref={filterButtonRef} onClick={() => setIsFilterMenuOpen(true)}>
              <FilterIcon size={18} />
            </OptionsButton>
            {isFilterMenuOpen && (
              <FilterMenu
                headers={compositionHeaders}
                onApplyFilter={(f) => {
                  onApplyFilter(f)
                  setIsFilterMenuOpen(false)
                }}
                onClose={() => setIsFilterMenuOpen(false)}
                anchorRef={filterButtonRef}
                filters={appliedFilters}
              />
            )}
          </FilterWrapper>
        )}
        {!isFormPage && viewMode === 'table' && (
          <ButtonCreate onClick={() => navigate('/panel/composition-product/create')}>
            <Plus size={18} />
            Novo produto
          </ButtonCreate>
        )}
      </RightSide>
    </SubHeaderContainer>
  )
}

const ProductCompositionContent = () => {
  const [refresh, setRefresh] = useState(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [viewMode, setViewMode] = useState<'board' | 'table'>(() => {
    const persistedMode = localStorage.getItem(COMPOSITION_VIEW_MODE_STORAGE_KEY)
    return persistedMode === 'board' ? 'board' : 'table'
  })
  const [productFilters, setProductFilters] = useState<ProductDevelopmentProductFilters>(
    EMPTY_PRODUCT_FILTERS,
  )
  const mainOutletContext = useOutletContext<TypeMainOutlet | undefined>()
  const search = mainOutletContext?.search || ''
  const location = useLocation()

  const context = useOutletContext<any>()
  const compositionFiltersState = context?.compositionFiltersState || usePersistedFilters('composition_product')

  const isFormPage =
    location.pathname.includes('/panel/composition-product/create') ||
    location.pathname.includes('/panel/composition-product/edit')

  const isInsideProductDevelopment = location.pathname.startsWith('/panel/composition-product') || location.pathname.startsWith('/panel/product-development')

  useEffect(() => {
    const handleProductDevelopmentRefresh = (event: Event) => {
      const detail = (event as CustomEvent<{ tab?: string }>).detail
      if (detail?.tab === 'composition') {
        setRefresh(previous => !previous)
      }
    }

    const handleProductDevelopmentViewMode = (event: Event) => {
      const detail = (event as CustomEvent<{ tab?: string; viewMode?: 'board' | 'table' }>).detail
      if (detail?.tab !== 'composition') {
        return
      }

      if (detail?.viewMode === 'board' || detail?.viewMode === 'table') {
        setViewMode(detail.viewMode)
      }
    }

    const handleProductDevelopmentFilters = (event: Event) => {
      const detail = (event as CustomEvent<{ filters?: ProductDevelopmentProductFilters }>).detail
      if (!detail?.filters) {
        return
      }
      setProductFilters(detail.filters)
    }

    window.addEventListener('product-development-refresh', handleProductDevelopmentRefresh as EventListener)
    window.addEventListener('product-development-view-mode', handleProductDevelopmentViewMode as EventListener)
    window.addEventListener('product-development-filters', handleProductDevelopmentFilters as EventListener)

    return () => {
      window.removeEventListener('product-development-refresh', handleProductDevelopmentRefresh as EventListener)
      window.removeEventListener('product-development-view-mode', handleProductDevelopmentViewMode as EventListener)
      window.removeEventListener('product-development-filters', handleProductDevelopmentFilters as EventListener)
    }
  }, [])

  useEffect(() => {
    localStorage.setItem(COMPOSITION_VIEW_MODE_STORAGE_KEY, viewMode)
  }, [viewMode])

  const outletContext: ProductCompositionOutletType = useMemo(
    () => ({
      refresh,
      search,
      viewMode,
      productFilters,
      appliedFilters: compositionFiltersState.appliedFilters,
      isLoading,
      setIsLoading,
    }),
    [refresh, search, viewMode, productFilters, compositionFiltersState.appliedFilters, isLoading],
  )

  return (
    <>
      {!isFormPage && !isInsideProductDevelopment && (
        <ProductCompositionSubHeader
          setRefresh={setRefresh}
          isLoading={isLoading}
          viewMode={viewMode}
          setViewMode={setViewMode}
          appliedFilters={compositionFiltersState.appliedFilters}
          onApplyFilter={compositionFiltersState.handleApplyFilter}
        />
      )}
      {!isFormPage && !isInsideProductDevelopment && compositionFiltersState.appliedFilters.length > 0 && (
        <AppliedFiltersDisplay
          filters={compositionFiltersState.appliedFilters}
          onRemoveFilter={compositionFiltersState.handleRemoveFilter}
          onClearAll={compositionFiltersState.handleClearAll}
        />
      )}
      <Content>
        <Outlet context={outletContext} />
      </Content>
    </>
  )
}

export default ProductCompositionContent